import React, { useState, useEffect, useRef } from 'react';
import { Input, Label, Button } from 'reactstrap';
import { clone } from '../../../../../Services/schedule';

import style from './style';

const CustomDurationPopover = ({
  row,
  scheduleData,
  addRowCustomEvent,
  hideHeaderPopover,
  setShowCustomDurationPopover,
  fireConfirmationForEditingPastSchedule,
  dateIsInThePast,
  willEditThePast,
  showPastScheduleWarning,
  isOpen,
  onScheduleTimeShift,
}) => {
  const [event, setEvent] = useState({});
  const durationMinutesInputRef = useRef(null);
  const { metaData = {} } = scheduleData || {};

  useEffect(() => {
    reset(row);
  }, [JSON.stringify(row)]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const timer = setTimeout(() => {
      durationMinutesInputRef.current && durationMinutesInputRef.current.focus();
    }, 0);

    return () => clearTimeout(timer);
  }, [isOpen]);

  const reset = (row) => {
    if (row) {
      const {
        CustomDurationInMinutes,
        CustomMeetingDescription,
        CustomMeetingTitle,
        CustomMeetingUrl,
        CustomPassingDurationInMinutes,
        VirtualRoomType,
      } = row;

      const duration = CustomDurationInMinutes != null ? CustomDurationInMinutes : metaData.StandardDurationInMinutes;
      const passingDuration =
        CustomPassingDurationInMinutes != null
          ? CustomPassingDurationInMinutes
          : metaData.StandardPassingDurationInMinutes;

      setEvent({
        CustomDurationInMinutes: duration,
        CustomMeetingDescription,
        CustomMeetingTitle,
        CustomMeetingUrl,
        CustomPassingDurationInMinutes: passingDuration,
        VirtualRoomType,
        fk_MeetingRoom: undefined,
        pk_Timeslot: row.pk_Timeslot,
      });
    }
  };

  const getEffectiveDuration = (durationValue) => {
    if (durationValue == null || durationValue === '') {
      return parseInt(metaData.StandardDurationInMinutes) || 0;
    }

    return parseInt(durationValue) || 0;
  };

  const getEffectivePassingDuration = (passingValue) => {
    if (passingValue == null || passingValue === '') {
      return parseInt(metaData.StandardPassingDurationInMinutes) || 0;
    }

    return parseInt(passingValue) || 0;
  };

  const getRowTotalMinutes = (durationValue, passingValue) => {
    return getEffectiveDuration(durationValue) + getEffectivePassingDuration(passingValue);
  };

  const saveDurationChange = async (newEvent, options = {}) => {
    const { closeAfterSave = false, syncLocalState = false } = options;
    const oldTotalMinutes = getRowTotalMinutes(row && row.CustomDurationInMinutes, row && row.CustomPassingDurationInMinutes);
    const newTotalMinutes = getRowTotalMinutes(newEvent.CustomDurationInMinutes, newEvent.CustomPassingDurationInMinutes);
    const deltaMinutes = newTotalMinutes - oldTotalMinutes;

    console.log('[Schedule Debug] CustomDuration saveDurationChange start', {
      rowPk: row ? row.pk_Timeslot : null,
      rowSlot: row ? row.slot : null,
      rowSlotEnd: row ? row.slotEnd : null,
      oldDuration: row ? row.CustomDurationInMinutes : null,
      oldPassing: row ? row.CustomPassingDurationInMinutes : null,
      newDuration: newEvent.CustomDurationInMinutes,
      newPassing: newEvent.CustomPassingDurationInMinutes,
      oldTotalMinutes,
      newTotalMinutes,
      deltaMinutes,
      closeAfterSave,
      syncLocalState,
    });

    addRowCustomEvent(newEvent, null, null, async (err) => {
      console.log('[Schedule Debug] CustomDuration addRowCustomEvent callback', {
        hasError: !!err,
        err,
        deltaMinutes,
      });

      if (err) {
        return;
      }

      if (onScheduleTimeShift && deltaMinutes !== 0 && row && row.slotEnd) {
        console.log('[Schedule Debug] CustomDuration calling onScheduleTimeShift', {
          deltaMinutes,
          anchorDateTime: row.slotEnd,
        });
        await onScheduleTimeShift({
          deltaMinutes,
          anchorDateTime: row.slotEnd,
        });
      } else {
        console.log('[Schedule Debug] CustomDuration NOT calling onScheduleTimeShift', {
          hasOnScheduleTimeShift: !!onScheduleTimeShift,
          deltaMinutes,
          rowSlotEnd: row ? row.slotEnd : null,
        });
      }

      if (syncLocalState) {
        setEvent(newEvent);
      }

      if (closeAfterSave) {
        hideHeaderPopover();
      }
    });
  };

  return (
    <div
      style={style.mainBody}
      onClick={async (e) => {
        if (dateIsInThePast && !willEditThePast) {
          const continueProcess = await fireConfirmationForEditingPastSchedule();
          if (!continueProcess) {
            return;
          }
        }
        e.stopPropagation();
      }}
    >
      <div>Default duration is {metaData.StandardDurationInMinutes || 8} minutes.</div>
      <div style={style.simpleRow}>
        <div style={style.simpleColumn}>
          <Label style={style.inputLabel} for="durationMinutes">
            Duration in Minutes:
          </Label>
          <Input
            innerRef={durationMinutesInputRef}
            type="number"
            style={style.inputFields}
            name="durationMinutes"
            id="durationMinutes"
            value={event.CustomDurationInMinutes}
            placeholder=""
            defaultValue={metaData.StandardDurationInMinutes}
            onChange={(e) => {
              const newEvent = clone(event);
              let newValue = e.target.value;

              if (newValue < 1) {
                newValue = 1;
              }

              newEvent.CustomDurationInMinutes = newValue;
              setEvent(newEvent);
            }}
          />
        </div>
      </div>
      <div style={style.simpleRow}>
        <div style={style.simpleColumn}>
          <Label style={style.inputLabel} for="passingMinutes">
            Passing Period in Minutes:
          </Label>
          <Input
            type="number"
            style={style.inputFields}
            name="passingMinutes"
            id="passingMinutes"
            value={event.CustomPassingDurationInMinutes}
            placeholder=""
            defaultValue={metaData.StandardPassingDurationInMinutes}
            onChange={(e) => {
              const newEvent = clone(event);
              let newValue = e.target.value;

              if (newValue < 0) {
                newValue = 0;
              }

              newEvent.CustomPassingDurationInMinutes = newValue;
              setEvent(newEvent);
            }}
          />
        </div>
      </div>
      <div style={style.spacedBetweenRow}>
        <Button
          color="danger"
          style={{ ...style.genButton, backgroundColor: null }}
          onClick={async () => {
            setShowCustomDurationPopover(false);
          }}
        >
          <div style={style.buttonLabel}>Cancel</div>
        </Button>
        <Button
          style={style.genButton}
          onClick={async () => {
            if (dateIsInThePast && !willEditThePast) {
              const continueProcess = await fireConfirmationForEditingPastSchedule();
              if (!continueProcess) {
                return;
              }
            }
            const newEvent = clone(event);
            // newEvent.CustomDurationInMinutes = metaData.StandardDurationInMinutes;
            // newEvent.CustomPassingDurationInMinutes = metaData.StandardPassingDurationInMinutes;

            newEvent.CustomDurationInMinutes = null;
            newEvent.CustomPassingDurationInMinutes = null;

            await saveDurationChange(newEvent, { syncLocalState: true });
          }}
        >
          <div style={style.buttonLabel}>Reset</div>
        </Button>
        <Button
          style={style.genButton}
          onClick={async () => {
            if (dateIsInThePast && !willEditThePast) {
              const continueProcess = await fireConfirmationForEditingPastSchedule();
              if (!continueProcess) {
                return;
              }
            }
            const newEvent = clone(event);
            if (newEvent.CustomDurationInMinutes == metaData.StandardDurationInMinutes) {
              newEvent.CustomDurationInMinutes = null;
            }

            if (newEvent.CustomPassingDurationInMinutes == metaData.StandardPassingDurationInMinutes) {
              newEvent.CustomPassingDurationInMinutes = null;
            }
            await saveDurationChange(newEvent, { closeAfterSave: true });
          }}
        >
          <div style={style.buttonLabel}>Set</div>
        </Button>
      </div>
    </div>
  );
};

export default CustomDurationPopover;
